/**
 * Case-by-case matrix: one row per shared case, one column per run, divergence highlighted.
 * Virtualised (antd native `virtual`) so thousands of rows stay smooth. Defaults to the
 * "Divergent" filter so the first thing shown is where the runs actually differ. Clicking a
 * row opens the drilldown drawer, which can then step through the filtered set.
 */
import { useMemo, useState } from "react";
import {
  Table,
  Tag,
  Input,
  Segmented,
  Tooltip,
  type TableColumnsType,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { BenchmarkResult } from "@/lib/api/resources/benchmark";
import {
  isErrored,
  type CompareMatrix,
  type CompareRow,
  type CompareRun,
} from "../lib/align";
import { ReferenceViewer } from "../../components/ReferenceViewer";
import { formatOutput } from "../../components/RunCasesTable";
import { CaseCompareDrawer } from "./CaseCompareDrawer";

type FilterKey = "divergent" | "all" | "regressions" | "errors";

const worseThanBaseline = (
  cell: BenchmarkResult | null,
  base: BenchmarkResult | null,
): boolean => {
  if (!cell || !base) return false;
  if (isErrored(cell) && !isErrored(base)) return true;
  if (base.correct === true && cell.correct === false) return true;
  if (
    cell.qualityScore != null &&
    base.qualityScore != null &&
    base.qualityScore - cell.qualityScore >= 2
  ) {
    return true;
  }
  return false;
};

const JudgeChip = ({ score }: { score: number }) => {
  const color = score >= 8 ? "#10b981" : score >= 5 ? "#f59e0b" : "#ef4444";
  return (
    <span style={{ color, fontWeight: 700 }} className="text-xs">
      {score}/10
    </span>
  );
};

const RunCell = ({ c }: { c: BenchmarkResult | null }) => {
  if (c == null) return <span className="text-gray-300 text-xs">not run</span>;
  return (
    <div className="flex items-center gap-2">
      {isErrored(c) ? (
        <Tag color="error" className="!m-0">
          Error
        </Tag>
      ) : c.correct === true ? (
        <Tag color="success" className="!m-0">
          Match
        </Tag>
      ) : c.correct === false ? (
        <Tag color="warning" className="!m-0">
          Miss
        </Tag>
      ) : (
        <Tag className="!m-0">—</Tag>
      )}
      {c.qualityScore != null && <JudgeChip score={c.qualityScore} />}
      {c.latencyMs != null && (
        <span className="text-[10px] text-gray-400 font-mono">
          {c.latencyMs}ms
        </span>
      )}
    </div>
  );
};

export const CompareCasesTable = ({
  runs,
  matrix,
  baselineId,
  unalignable,
}: {
  runs: CompareRun[];
  matrix: CompareMatrix;
  baselineId: string;
  unalignable: number;
}) => {
  const [filter, setFilter] = useState<FilterKey>("divergent");
  const [search, setSearch] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);

  const counts = useMemo(() => {
    const base = baselineId;
    return {
      all: matrix.rows.length,
      divergent: matrix.rows.filter((r) => r.divergent).length,
      errors: matrix.rows.filter((r) => r.hasError).length,
      regressions: matrix.rows.filter((r) =>
        runs.some(
          (run) =>
            run.id !== base &&
            worseThanBaseline(r.cells[run.id], r.cells[base]),
        ),
      ).length,
    };
  }, [matrix.rows, runs, baselineId]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return matrix.rows.filter((r) => {
      if (
        q &&
        !r.label.toLowerCase().includes(q) &&
        !r.key.toLowerCase().includes(q)
      ) {
        return false;
      }
      if (filter === "divergent") return r.divergent;
      if (filter === "errors") return r.hasError;
      if (filter === "regressions") {
        return runs.some(
          (run) =>
            run.id !== baselineId &&
            worseThanBaseline(r.cells[run.id], r.cells[baselineId]),
        );
      }
      return true;
    });
  }, [matrix.rows, filter, search, runs, baselineId]);

  // If the active filter is empty, fall back to All so the user is never staring at a blank grid.
  const effectiveRows =
    rows.length === 0 && counts.all > 0 && filter !== "all"
      ? matrix.rows
      : rows;

  // Track the open case by key (not index) so it stays put when the matrix re-sorts on a poll.
  const openIndex =
    openKey != null ? effectiveRows.findIndex((r) => r.key === openKey) : -1;
  const openRow = openIndex >= 0 ? effectiveRows[openIndex] : null;

  const columns: TableColumnsType<CompareRow> = [
    {
      title: "Input case",
      dataIndex: "label",
      key: "input",
      width: 260,
      fixed: "left",
      render: (_: unknown, row: CompareRow) => {
        const firstCell =
          runs.map((r) => row.cells[r.id]).find(Boolean) ?? null;
        return (
          <div onClick={(e) => e.stopPropagation()} className="cursor-default">
            {firstCell ? (
              <ReferenceViewer result={firstCell} />
            ) : (
              <span className="font-mono text-xs">{row.label}</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Expected (Ground Truth)",
      key: "expected",
      width: 200,
      fixed: "left",
      render: (_: unknown, row: CompareRow) => {
        const firstCell =
          runs.map((r) => row.cells[r.id]).find(Boolean) ?? null;
        if (!firstCell || firstCell.expectedOutput == null) {
          return <span className="text-gray-400 text-xs">—</span>;
        }

        return (
          <div className="text-xs text-gray-700 max-h-[72px] overflow-y-auto whitespace-pre-wrap break-all pr-1">
            {formatOutput(firstCell.expectedOutput)}
          </div>
        );
      },
    },
    ...runs.map((run) => ({
      title: (
        <span className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full inline-block"
            style={{ backgroundColor: run.color }}
          />
          <span className="truncate max-w-[8rem]" title={run.label}>
            {run.label}
          </span>
          {run.id === baselineId && (
            <span className="text-[10px] text-gray-400">(base)</span>
          )}
        </span>
      ),
      key: run.id,
      width: 180,
      render: (_: unknown, row: CompareRow) => (
        <RunCell c={row.cells[run.id]} />
      ),
    })),
    {
      title: "",
      key: "divergence",
      width: 70,
      render: (_: unknown, row: CompareRow) =>
        row.divergent ? (
          <Tooltip title="Runs disagree on this case">
            <Tag color="orange" className="!m-0">
              Δ
            </Tag>
          </Tooltip>
        ) : null,
    },
  ];

  const scrollX = 260 + runs.length * 180 + 70;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <Segmented
          value={filter}
          onChange={(v) => setFilter(v as FilterKey)}
          options={[
            { label: `Divergent (${counts.divergent})`, value: "divergent" },
            {
              label: `Regressions (${counts.regressions})`,
              value: "regressions",
            },
            { label: `Errors (${counts.errors})`, value: "errors" },
            { label: `All (${counts.all})`, value: "all" },
          ]}
        />
        <Input
          allowClear
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder="Search case…"
          className="max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {rows.length === 0 && counts.all > 0 && filter !== "all" && (
        <div className="text-xs text-gray-400 mb-2">
          No cases match “{filter}”. Showing all cases instead.
        </div>
      )}

      <Table<CompareRow>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={effectiveRows}
        virtual
        scroll={{ x: scrollX, y: 540 }}
        pagination={false}
        rowClassName={(row) =>
          `cursor-pointer ${row.divergent ? "bg-orange-50/40" : ""}`
        }
        onRow={(row) => ({
          onClick: () => setOpenKey(row.key),
        })}
      />

      {unalignable > 0 && (
        <div className="text-xs text-gray-400 mt-2">
          {unalignable} result{unalignable === 1 ? "" : "s"} could not be
          matched across runs (no test-case id or input reference) and{" "}
          {unalignable === 1 ? "is" : "are"} excluded.
        </div>
      )}

      <CaseCompareDrawer
        open={openRow != null}
        row={openRow}
        runs={runs}
        baselineId={baselineId}
        index={openIndex}
        total={effectiveRows.length}
        onClose={() => setOpenKey(null)}
        onNavigate={(delta) => {
          const next = effectiveRows[openIndex + delta];
          if (next) setOpenKey(next.key);
        }}
      />
    </div>
  );
};

export default CompareCasesTable;
